/**
 * `AddLayerToGTwinDialog` (WP-1).
 *
 * Replaces the legacy in-shell `AddLayerDialog` (which wrote `devPreview`
 * layers in-memory only) with the gTwin-first writer described in
 * masterplan §5.2:
 *
 *   1. Validates the input artifactKind / endpoint URL / source CRS / bbox
 *      against the canonical contract in `artifactContract.ts`.
 *   2. Computes the canonical `SceneItemInput` per
 *      `SCENE_PACKAGE_CONTRACT.md` for COG, GLB, COPC, 3D Tiles, GeoJSON,
 *      and the additional kinds enumerated in `artifactContract.ts`.
 *   3. Calls `gtwinClient.scene.items.add(gtwinId, item)` via the WP-1
 *      adapter (mock under `VITE_GTWIN_SCENE_ITEMS=mock`).
 *   4. On success, returns the canonical `SceneItem` so the caller can
 *      add it to the running scene and re-render.
 *
 * Audit forwarding is handled inside the mock adapter (`recordAudit` from
 * `audit.ts`); the dialog itself does not need to log.
 *
 * Token-only: every visual constant is sourced from `tokens.*` or the
 * `Dialog`/`Banner`/`Icon`/`Button` design primitives. No emoji.
 */

import { useEffect, useId, useMemo, useState } from 'react';
import {
  Banner,
  Dialog,
  Icon,
  tokens,
  type DialogAction,
} from '@/design';
import type {
  ArtifactKind,
  SceneItem,
  SceneItemInput,
} from '@/sdk-stub/gtwin-sdk';
import {
  ARTIFACT_KIND_OPTIONS,
  buildSceneItemInput,
  type AddLayerFormState,
} from './buildSceneItemInput';

export interface AddLayerToGTwinDialogProps {
  readonly gtwinId: string;
  /** Adapter call. Pre-bound to `gtwinClient.scene.items.add` by the caller. */
  readonly onAdd: (gtwinId: string, item: SceneItemInput) => Promise<SceneItem>;
  readonly onSuccess: (item: SceneItem, input: SceneItemInput) => void;
  readonly onClose: () => void;
  /** Optional initial form state; used by deep-link handoffs. */
  readonly initialForm?: Partial<AddLayerFormState>;
  readonly 'data-testid'?: string;
}

const inputStyle = {
  width: '100%',
  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
  borderRadius: tokens.radius.input,
  border: tokens.border.thin,
  background: tokens.color.surface[0],
  color: tokens.color.ink.primary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  minHeight: tokens.density.touchMin,
  boxSizing: 'border-box' as const,
};

const labelStyle = {
  display: 'block',
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.role.label.size,
  fontWeight: tokens.font.weight.medium,
  color: tokens.color.ink.secondary,
  marginBottom: tokens.space[1],
};

const fieldStackStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: tokens.space[3],
};

const helpRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[2],
  fontSize: tokens.font.role.engMeta.size,
  color: tokens.color.ink.muted,
  fontFamily: tokens.font.family.ui,
};

const splitRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: tokens.space[3],
};

function parseBbox(raw: string): [number, number, number, number] | null | string {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const parts = trimmed.split(/[,\s]+/).filter(Boolean).map(Number);
  if (parts.length !== 4 || parts.some((value) => !Number.isFinite(value))) {
    return 'invalid';
  }
  return [parts[0], parts[1], parts[2], parts[3]];
}

export function AddLayerToGTwinDialog({
  gtwinId,
  onAdd,
  onSuccess,
  onClose,
  initialForm,
  'data-testid': testId,
}: AddLayerToGTwinDialogProps) {
  const formId = useId();
  const [artifactKind, setArtifactKind] = useState<ArtifactKind>(
    (initialForm?.artifactKind as ArtifactKind | undefined) ?? 'cog',
  );
  const [url, setUrl] = useState(initialForm?.url ?? '');
  const [title, setTitle] = useState(initialForm?.title ?? '');
  const [sourceCrs, setSourceCrs] = useState(initialForm?.sourceCrs ?? '');
  const [bboxRaw, setBboxRaw] = useState(
    initialForm?.bbox ? initialForm.bbox.join(', ') : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset form fields when initialForm prop changes (e.g. deep-link reuse).
  useEffect(() => {
    if (initialForm?.artifactKind) {
      setArtifactKind(initialForm.artifactKind as ArtifactKind);
    }
  }, [initialForm?.artifactKind]);

  const selectedOption = useMemo(
    () => ARTIFACT_KIND_OPTIONS.find((option) => option.artifactKind === artifactKind),
    [artifactKind],
  );

  const bboxParsed = parseBbox(bboxRaw);
  const bboxError = bboxParsed === 'invalid'
    ? 'bbox must be 4 finite numbers: minX, minY, maxX, maxY'
    : null;

  const previewBuild = useMemo(() => {
    if (!url.trim()) return null;
    const bboxForBuild =
      bboxParsed && bboxParsed !== 'invalid'
        ? ([bboxParsed[0], bboxParsed[1], bboxParsed[2], bboxParsed[3]] as [
            number,
            number,
            number,
            number,
          ])
        : null;
    return buildSceneItemInput({
      artifactKind,
      url,
      title,
      sourceCrs: sourceCrs || undefined,
      bbox: bboxForBuild,
    });
  }, [artifactKind, url, title, sourceCrs, bboxParsed]);

  const validationErrors = previewBuild?.errors ?? [];
  const submitDisabled =
    submitting
    || url.trim().length === 0
    || bboxError !== null
    || validationErrors.length > 0;

  async function handleSubmit() {
    if (submitDisabled || !previewBuild) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const item = await onAdd(gtwinId, previewBuild.input);
      onSuccess(item, previewBuild.input);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const actions: DialogAction[] = [
    { label: 'Cancel', variant: 'ghost', onClick: onClose },
    {
      label: submitting ? 'Adding…' : 'Add layer to project',
      variant: 'primary',
      onClick: handleSubmit,
      defaultFocus: false,
    },
  ];

  return (
    <Dialog
      title="Add layer to project"
      size="md"
      onClose={submitting ? () => undefined : onClose}
      actions={actions}
      data-testid={testId ?? 'add-layer-to-gtwin-dialog'}
    >
      <div style={fieldStackStyle}>
        <p
          style={{
            margin: 0,
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
          }}
        >
          The layer is persisted into the gTwin scene package via the platform
          SDK. The source artifact stays in storage; only a `SceneItem` ref is
          written into the project.
        </p>

        <div>
          <label htmlFor={`${formId}-kind`} style={labelStyle}>
            Artifact kind
          </label>
          <select
            id={`${formId}-kind`}
            value={artifactKind}
            onChange={(event) => setArtifactKind(event.currentTarget.value as ArtifactKind)}
            style={inputStyle}
            data-testid="add-layer-artifact-kind"
          >
            {ARTIFACT_KIND_OPTIONS.map((option) => (
              <option key={option.artifactKind} value={option.artifactKind}>
                {option.label}
              </option>
            ))}
          </select>
          {selectedOption ? (
            <div style={{ ...helpRowStyle, marginTop: tokens.space[1] }}>
              <Icon name="info" size={16} decorative />
              <span>
                Slot: <strong>{selectedOption.slot}</strong> · endpoint key:{' '}
                <code style={{ fontFamily: tokens.font.family.mono }}>
                  {previewBuild?.contract.endpointKey ?? selectedOption.artifactKind}
                </code>
              </span>
            </div>
          ) : null}
        </div>

        <div>
          <label htmlFor={`${formId}-url`} style={labelStyle}>
            Endpoint URL
          </label>
          <input
            id={`${formId}-url`}
            type="url"
            autoFocus
            value={url}
            onChange={(event) => setUrl(event.currentTarget.value)}
            placeholder={selectedOption?.placeholder}
            style={inputStyle}
            data-testid="add-layer-url"
          />
        </div>

        <div>
          <label htmlFor={`${formId}-title`} style={labelStyle}>
            Title (optional)
          </label>
          <input
            id={`${formId}-title`}
            type="text"
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            placeholder="Display label for the scene tree"
            style={inputStyle}
            data-testid="add-layer-title"
          />
        </div>

        <div style={splitRowStyle}>
          <div>
            <label htmlFor={`${formId}-crs`} style={labelStyle}>
              Source CRS (optional)
            </label>
            <input
              id={`${formId}-crs`}
              type="text"
              value={sourceCrs}
              onChange={(event) => setSourceCrs(event.currentTarget.value)}
              placeholder="EPSG:4326 or LOCAL"
              style={inputStyle}
              data-testid="add-layer-crs"
            />
          </div>
          <div>
            <label htmlFor={`${formId}-bbox`} style={labelStyle}>
              BBox (minX, minY, maxX, maxY)
            </label>
            <input
              id={`${formId}-bbox`}
              type="text"
              value={bboxRaw}
              onChange={(event) => setBboxRaw(event.currentTarget.value)}
              placeholder="optional"
              style={inputStyle}
              data-testid="add-layer-bbox"
              aria-invalid={bboxError ? true : undefined}
            />
            {bboxError ? (
              <span
                style={{
                  display: 'block',
                  marginTop: tokens.space[1],
                  color: tokens.color.semantic.danger,
                  fontSize: tokens.font.role.engMeta.size,
                }}
              >
                {bboxError}
              </span>
            ) : null}
          </div>
        </div>

        {validationErrors.length > 0 ? (
          <Banner
            severity="warning"
            title="Scene item is not valid yet"
            body={
              <ul
                style={{
                  margin: 0,
                  paddingLeft: tokens.space[4],
                  listStyle: 'disc',
                }}
              >
                {validationErrors.map((error) => (
                  <li
                    key={`${error.field}-${error.message}`}
                    style={{ marginTop: tokens.space[1] }}
                  >
                    <strong>{error.field}</strong>: {error.message}
                  </li>
                ))}
              </ul>
            }
          />
        ) : null}

        {submitError ? (
          <Banner
            severity="error"
            title="Add layer failed"
            body={submitError}
            errorCode="WP1-ADD-LAYER"
          />
        ) : null}
      </div>
    </Dialog>
  );
}
